<template>
  <cv-accordion-item
    v-show="showTagging"
    :key="tagId"
    :open="open"
    class="resource-tagging-edit-item"
  >
    <template #title>
      <span class="title">
        {{ headerTitle }}
      </span>
      <span class="icon">
        <svg-icon
          v-if="!isAdaptiveSection || open"
          name="Trash24"
          kind="action"
          @click.stop.prevent
          @click="handleRemoveResourceTaggging"
        />
      </span>
    </template>
    <template #content>
      <resource-tagging-selection
        v-model:tagging-selection="taggingSelection"
        :resource-tagging="resourceTagging"
        :resource-type="resourceType"
        :allow-multiple-select-topic="allowMultipleSelectTopic"
        :is-adaptive-section="isAdaptiveSection"
        :content-map-warning-context="contentMapWarningContext"
        @change-selection="handleChangeSelection"
        @update-tagging="handleUpdateTagging"
      />
    </template>
  </cv-accordion-item>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { NAMESPACE } from "@modules/editor/service/editor.constant";
import { isEmpty, isDefined, isNull } from "@/utils/var.check";
import ResourceTaggingSelection from "@modules/resource-tagging-selection/components/ResourceTaggingSelection.vue";
import taggingStore from "@modules/resource-tagging-selection/components/ResourceTaggingSelectionStore";
import resourceTaggingsStore from "@modules/editor/components/resource-taggings/ResourceTaggingsStore";
import ROLE from "@configs/role";
import ObjectUtils from "@utils/object.utils";
import { useStore } from "vuex";
import { useDirty } from "@/plugins/common/composables/page-dirty";
import type { ContentMapWarningContext } from "@modules/resource-tagging-selection/service/resource.tagging.selection.constant.ts";

// Props declaration
interface Props {
  open?: boolean;
  resourceTagging?: Record<string, unknown> | null;
  resourceType: string;
  resourceUuid: string;
  allowMultipleSelectTopic?: boolean;
  isAdaptiveSection?: boolean;
  contentMapWarningContext?: ContentMapWarningContext;
}

const props = withDefaults(defineProps<Props>(), {
  open: false,
  resourceTagging: null,
  allowMultipleSelectTopic: true,
  isAdaptiveSection: false,
  contentMapWarningContext: undefined,
});

// Emits declaration
const emit = defineEmits<{
  "remove-tagging": [tagId: unknown];
}>();

// composables
const processingId = computed(() => {
  return id.value;
});
const { t } = useI18n();
const store = useStore();
const { setDirty } = useDirty(processingId);

// ref/computed
const showTagging = ref(true);
const taggingSelection = ref({
  subjectSelected: props.resourceTagging?.subjectId
    ? props.resourceTagging.subjectId.toString()
    : "", //subjectId
  levelAndStreamSelected: !isEmpty(props.resourceTagging?.levelAndStream)
    ? props.resourceTagging?.levelAndStream.id.toString()
    : "", //levelAndStreamId
  syllabusSelected: !isEmpty(props.resourceTagging?.contentMapMetadata)
    ? props.resourceTagging?.contentMapMetadata.uuid.toString()
    : "", //contentMapUuid
  contentMapMetadataSelected: !isEmpty(props.resourceTagging?.contentMapMetadata)
    ? props.resourceTagging?.contentMapMetadata
    : {},
  //setting of cognitive level and topics selected are at created method
});
const taggingToUpdate = ref<Record<string, unknown>>({});

// Computed
const hasRole = computed(() => store.getters[`${NAMESPACE.AUTH}/hasRole`]);

const isContentApprover = computed(() => {
  return hasRole.value(ROLE.CONTENT_APPROVER);
});

const isContentOfficer = computed(() => {
  return hasRole.value(ROLE.CONTENT_OFFICER);
});

const id = computed(() => {
  return props.resourceUuid;
});

const tagId = computed(() => {
  return props.resourceTagging?.tagId;
});

const contentMapMetadata = computed(() => {
  return taggingSelection.value.contentMapMetadataSelected
    ? taggingSelection.value.contentMapMetadataSelected
    : {};
});

const contentMapTitle = computed(() => {
  return contentMapMetadata.value ? contentMapMetadata.value.title : "";
});

const taggingCount = computed(() => {
  return !isEmpty(taggingSelection.value.topicsSelected)
    ? taggingSelection.value.topicsSelected.length
    : 0;
});

const headerTitle = computed(() => {
  if (isEmpty(props.resourceTagging)) {
    return t("content_map_label");
  }

  if (!isEmpty(syllabusLabel.value)) {
    return syllabusLabel.value + " - " + taggingCount.value + " selected";
  }

  if (props.isAdaptiveSection) {
    return t("adaptive_learning_content_map_label");
  }

  return t("content_map_label");
});

const syllabusLabel = computed(() => {
  const syllabusName = contentMapMetadata.value ? contentMapMetadata.value.syllabus : "";
  let syllabusLabel = contentMapTitle.value;
  if (!isEmpty(syllabusName)) {
    syllabusLabel = syllabusLabel.concat(` - ${syllabusName}`);
  }

  return syllabusLabel;
});

// Methods
const handleRemoveResourceTaggging = function () {
  setDirty(processingId.value);
  // reset old resource tagging isFullyTagged status
  resetOldTaggingFullyTaggedStatus();

  emit("remove-tagging", tagId.value);
};

const handleChangeSelection = function () {
  setDirty(processingId.value);
};

const handleUpdateTagging = function () {
  populateTaggingDataToUpdate();
  updateResourceTagging();
};

const populateTaggingDataToUpdate = function () {
  taggingToUpdate.value = {};
  taggingToUpdate.value.tagId = tagId.value;

  if (!isEmpty(taggingSelection.value.subjectSelected)) {
    taggingToUpdate.value.subjectId = parseInt(taggingSelection.value.subjectSelected);
  }

  if (!isEmpty(taggingSelection.value.levelAndStreamSelected)) {
    taggingToUpdate.value.levelAndStream = {
      id: parseInt(taggingSelection.value.levelAndStreamSelected),
    };
  }

  if (!isEmpty(contentMapMetadata.value)) {
    taggingToUpdate.value.contentMapMetadata = {
      title: contentMapMetadata.value.title,
      uuid: contentMapMetadata.value.uuid,
      expired: contentMapMetadata.value.expired,
      started: contentMapMetadata.value.started,
      viewableByTeachers: contentMapMetadata.value.viewableByTeachers,
      ownerGroupId: contentMapMetadata.value.ownerGroupId || contentMapMetadata.value.ownerGroup,
      als: contentMapMetadata.value.als,
    };
  }

  if (!isEmpty(taggingSelection.value.topicsSelected)) {
    taggingToUpdate.value.taggings = taggingSelection.value.topicsSelected;
  }

  if (
    !isEmpty(taggingSelection.value.cognitiveLevelSelected) &&
    taggingSelection.value.cognitiveLevelSelected !== "-1"
  ) {
    const taggings = isDefined(taggingToUpdate.value.taggings)
      ? ObjectUtils.deepClone(taggingToUpdate.value.taggings)
      : [];
    taggings.push({
      cognitiveLevelId: parseInt(taggingSelection.value.cognitiveLevelSelected),
    });
    taggingToUpdate.value.taggings = taggings;
  }
};

const updateResourceTagging = function () {
  // reset old resource tagging isFullyTagged status
  resetOldTaggingFullyTaggedStatus();

  // set isFullyTagged status according to new changed
  setNewTaggingFullyTaggedStatus();

  // replace with new resource tagging
  resourceTaggingsStore.addContentMapTagging(taggingToUpdate.value);
};

const resetOldTaggingFullyTaggedStatus = function () {
  const oldTagging = resourceTaggingsStore.getContentMapTaggingByTagId(tagId.value);

  if (isEmpty(oldTagging) || isEmpty(oldTagging.contentMapMetadata)) {
    return;
  }

  resourceTaggingsStore.resetTaggingFullyTaggedStatus({
    oldTagging: oldTagging,
    store: taggingStore,
  });
};

const setNewTaggingFullyTaggedStatus = function () {
  const newTagging = taggingToUpdate.value;

  if (isEmpty(newTagging.contentMapMetadata)) {
    return;
  }

  resourceTaggingsStore.updateTaggingFullyTaggedStatus({
    newTagging: newTagging,
    store: taggingStore,
  });
};

const hideResourceTaggingWithoutAccessRight = function () {
  if (isContentApprover.value || isContentOfficer.value) {
    if (
      !isEmpty(props.resourceTagging) &&
      !isEmpty(props.resourceTagging?.contentMapMetadata) &&
      props.resourceTagging.contentMapMetadata.expired
    ) {
      showTagging.value = false;
    }
  } else {
    if (
      !isEmpty(props.resourceTagging) &&
      !isEmpty(props.resourceTagging.contentMapMetadata) &&
      (props.resourceTagging.contentMapMetadata.expired ||
        (!props.resourceTagging.contentMapMetadata.started &&
          !props.resourceTagging.contentMapMetadata.viewableByTeachers))
    ) {
      showTagging.value = false;
    }
  }
};

// Created
//set taggingSelection's cognitive level and topics
if (!isEmpty(props.resourceTagging?.taggings)) {
  const cognitiveLevelTagging = props.resourceTagging?.taggings.find(
    (tagging) => isDefined(tagging.cognitiveLevelId) && !isNull(tagging.cognitiveLevelId),
  );
  if (isDefined(cognitiveLevelTagging)) {
    taggingSelection.value.cognitiveLevelSelected =
      cognitiveLevelTagging.cognitiveLevelId.toString();
  }

  taggingSelection.value.topicsSelected = props.resourceTagging?.taggings.filter((tagging) => {
    return !isDefined(tagging.cognitiveLevelId) || isNull(tagging.cognitiveLevelId);
  });
}

hideResourceTaggingWithoutAccessRight();
</script>
